// src/components/AntaresAIChat.tsx
import { Bot, Loader2, MessageCircle, Send, X } from 'lucide-react';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5275';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

// Gera (ou reutiliza) um pseudo-telefone único por sessão de browser
function getSessionPhone(): string {
  const key = 'antares_ai_phone';
  let phone = sessionStorage.getItem(key);
  if (!phone) {
    phone = `web_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(key, phone);
  }
  return phone;
}

const SUGGESTIONS = [
  '🤖 Automação de WhatsApp',
  '📅 Bot de agendamento',
  '🛒 Sistema para loja/petshop',
  '🌐 Landing page profissional',
  '📊 ERP sob medida',
  '🎯 Agendar demonstração gratuita',
];

export default function AntaresAIChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content:
        'Olá! 👋 Sou o assistente da **Antares Tecnologia GTA**. Posso te ajudar a entender nossas soluções e agendar uma demonstração gratuita.\n\nComo posso te ajudar hoje?',
    },
  ]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [phone] = useState(getSessionPhone);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, messages]);

  const send = async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;

    setShowSuggestions(false);
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/antares-ai/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, message: text }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            '😕 Desculpe, tive um problema ao processar sua mensagem. Tente novamente ou fale conosco pelo WhatsApp.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Botão flutuante */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Fechar chat' : 'Abrir chat com IA'}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full shadow-[0_0_28px_rgba(233,30,140,0.5)] bg-gradient-to-br from-[#E91E8C] to-[#7C3AED] text-white transition-all hover:brightness-110 active:scale-95"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {/* Janela do chat */}
      {open && (
        <div className="fixed bottom-24 right-6 z-50 flex w-[90vw] max-w-sm flex-col rounded-2xl border border-white/10 bg-[#0d1017] shadow-[0_24px_64px_rgba(0,0,0,0.6)] overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-3 bg-gradient-to-r from-[#E91E8C] to-[#7C3AED] px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white leading-none">Antares AI</p>
              <p className="text-xs text-white/70 mt-0.5">Assistente comercial</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="rounded-lg p-1 hover:bg-white/10 transition"
              aria-label="Fechar"
            >
              <X className="h-4 w-4 text-white" />
            </button>
          </div>

          {/* Mensagens */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 max-h-[380px] scrollbar-thin scrollbar-thumb-white/10">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="mr-2 mt-1 flex-shrink-0 flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-[#E91E8C] to-[#7C3AED]">
                    <Bot className="h-3.5 w-3.5 text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-br from-[#E91E8C] to-[#7C3AED] text-white rounded-br-sm'
                      : 'bg-white/8 border border-white/10 text-white/90 rounded-bl-sm'
                  }`}
                  dangerouslySetInnerHTML={{ __html: formatMarkdown(msg.content) }}
                />
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="mr-2 mt-1 flex-shrink-0 flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-[#E91E8C] to-[#7C3AED]">
                  <Bot className="h-3.5 w-3.5 text-white" />
                </div>
                <div className="flex items-center gap-2 rounded-2xl rounded-bl-sm bg-white/8 border border-white/10 px-4 py-2.5">
                  <Loader2 className="h-4 w-4 text-white/50 animate-spin" />
                  <span className="text-xs text-white/50">Digitando...</span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Sugestões */}
          {showSuggestions && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-xs rounded-full border border-[#E91E8C]/40 bg-[#E91E8C]/10 text-white/80 px-3 py-1.5 hover:bg-[#E91E8C]/25 hover:border-[#E91E8C]/70 transition active:scale-95"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="border-t border-white/10 px-3 py-3 flex items-center gap-2 bg-[#0d1017]">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Digite sua mensagem..."
              disabled={loading}
              className="flex-1 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-[#E91E8C]/50 focus:bg-white/8 transition disabled:opacity-50"
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              aria-label="Enviar"
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#E91E8C] to-[#7C3AED] text-white transition hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** Converte **negrito** e quebras de linha em HTML seguro */
function formatMarkdown(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>');
}
